import { API_BASE_URL } from './constants'
import { useAuthStore } from '../stores/authStore'

interface RequestOptions extends RequestInit {
  token?: string
}

export async function apiRequest<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<{ data: T }> {
  const { token, ...fetchOptions } = options

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }

  // Get token from store or localStorage
  const storedToken = token || useAuthStore.getState().token || localStorage.getItem('auth_token')
  
  if (storedToken) {
    headers['Authorization'] = `Bearer ${storedToken}`
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...fetchOptions,
    headers,
    credentials: 'include', // Include cookies for auth
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      error: 'An error occurred',
      code: 'UNKNOWN_ERROR',
      status: response.status,
    }))
    throw new Error(error.error || 'Request failed')
  }

  return response.json()
}

export const api = {
  // Auth
  verifyAuth: () => 
    fetch(`${API_BASE_URL.replace('analytics', 'auth')}/verify`, {
      credentials: 'include'
    }).then(r => r.json()),

  // Sites
  getSites: () =>
    apiRequest<any[]>('/api/sites'),
  
  createSite: (data: { domain: string; name: string }) =>
    apiRequest('/api/sites', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  updateSite: (id: string, data: { name?: string; settings?: any }) =>
    apiRequest(`/api/sites/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  deleteSite: (id: string) =>
    apiRequest(`/api/sites/${id}`, {
      method: 'DELETE',
    }),

  // Analytics
  getOverview: (params: { siteId: string; from: string; to: string; timezone?: string }) =>
    apiRequest(`/api/overview?${new URLSearchParams(params as any)}`),
  
  getPages: (params: { siteId: string; from: string; to: string; offset?: number; limit?: number }) =>
    apiRequest(`/api/pages?${new URLSearchParams(params as any)}`),
  
  getSources: (params: { siteId: string; from: string; to: string }) =>
    apiRequest(`/api/sources?${new URLSearchParams(params as any)}`),
  
  getReferrers: (params: { siteId: string; from: string; to: string; offset?: number; limit?: number }) =>
    apiRequest(`/api/referrers?${new URLSearchParams(params as any)}`),
  
  getCountries: (params: { siteId: string; from: string; to: string; offset?: number; limit?: number }) =>
    apiRequest(`/api/countries?${new URLSearchParams(params as any)}`),
  
  getDevices: (params: { siteId: string; from: string; to: string }) =>
    apiRequest(`/api/devices?${new URLSearchParams(params as any)}`),
  
  getBrowsers: (params: { siteId: string; from: string; to: string; offset?: number; limit?: number }) =>
    apiRequest(`/api/browsers?${new URLSearchParams(params as any)}`),
  
  getExperiments: (params: { siteId: string; from: string; to: string; test?: string }) =>
    apiRequest(`/api/experiments?${new URLSearchParams(params as any)}`),
  
  getRealtime: (params: { siteId: string }) =>
    apiRequest(`/api/realtime?${new URLSearchParams(params as any)}`),

  // Admin
  getUsers: () =>
    apiRequest('/api/users'),
  
  createUser: (data: { email: string; name: string; role: 'user' | 'admin' }) =>
    apiRequest('/api/users', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  deleteUser: (id: string) =>
    apiRequest(`/api/users/${id}`, {
      method: 'DELETE',
    }),
}
